/**
 * hook for keyboard navigation when selecting a move target position
 * manages position focus and scrolling during page move mode
 */
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import type { Virtualizer } from "@tanstack/react-virtual";

import { scrollToPage, scrollToPageSafe } from "../utils/virtualScrollUtils";

export const useMoveTargetNavigation = (
  isSelectingMoveTarget: boolean,
  totalPages: number,
  currentPage: number,
  onTargetSelect: ((targetPosition: number) => void) | null,
  onCancel: () => void,
  virtualizer: Virtualizer<HTMLDivElement, Element>,
  minPage: number = 1,
) => {
  const maxPosition = minPage + totalPages;
  const [focusedPosition, setFocusedPosition] = useState<number>(1);

  // start at current page position when entering selection mode
  useEffect(() => {
    if (!isSelectingMoveTarget) return;

    // position after current page (currentPage + 1)
    const initialPosition = Math.min(
      Math.max(currentPage + 1, minPage),
      maxPosition,
    );
    setFocusedPosition(initialPosition);

    // scroll to current page to keep context
    scrollToPageSafe({
      pageIndex: currentPage - minPage,
      totalPages,
      virtualizer,
    });
  }, [
    currentPage,
    isSelectingMoveTarget,
    maxPosition,
    minPage,
    totalPages,
    virtualizer,
  ]);

  // navigate up (to lower position number)
  useHotkeys(
    "up",
    (e) => {
      e.preventDefault();
      if (focusedPosition > minPage) {
        const newPosition = focusedPosition - 1;
        setFocusedPosition(newPosition);
        // scroll to keep the insertion point visible
        // position N corresponds to after page N-1, so scroll to page N-1
        const pageIndex = Math.max(0, newPosition - minPage);
        virtualizer.scrollToIndex(pageIndex, { align: "center" });
      }
    },
    { enabled: isSelectingMoveTarget },
    [focusedPosition, isSelectingMoveTarget, minPage, virtualizer],
  );

  // navigate down (to higher position number)
  useHotkeys(
    "down",
    (e) => {
      e.preventDefault();

      if (focusedPosition >= maxPosition) return;

      const newPosition = focusedPosition + 1;
      setFocusedPosition(newPosition);

      // scroll to keep the insertion point visible
      // position N corresponds to after page N-1, so scroll to page N-1
      const pageIndex = Math.min(totalPages - 1, newPosition - minPage);
      scrollToPage({
        pageIndex,
        totalPages,
        virtualizer,
      });
    },
    { enabled: isSelectingMoveTarget },
    [
      focusedPosition,
      isSelectingMoveTarget,
      maxPosition,
      minPage,
      totalPages,
      virtualizer,
    ],
  );

  // shift+up: jump 10 positions up (clamped to position 1)
  useHotkeys(
    "shift+up",
    (e) => {
      e.preventDefault();
      if (focusedPosition > minPage) {
        const newPosition = Math.max(minPage, focusedPosition - 10);
        setFocusedPosition(newPosition);
        const pageIndex = Math.max(0, newPosition - minPage);
        virtualizer.scrollToIndex(pageIndex, { align: "center" });
      }
    },
    { enabled: isSelectingMoveTarget },
    [focusedPosition, isSelectingMoveTarget, minPage, virtualizer],
  );

  // shift+down: jump 10 positions down (clamped to last position)
  useHotkeys(
    "shift+down",
    (e) => {
      e.preventDefault();

      if (focusedPosition >= maxPosition) return;

      const newPosition = Math.min(maxPosition, focusedPosition + 10);
      setFocusedPosition(newPosition);

      const pageIndex = Math.min(totalPages - 1, newPosition - minPage);
      scrollToPage({
        pageIndex,
        totalPages,
        virtualizer,
      });
    },
    { enabled: isSelectingMoveTarget },
    [
      focusedPosition,
      isSelectingMoveTarget,
      maxPosition,
      minPage,
      totalPages,
      virtualizer,
    ],
  );

  // select the focused position with Enter
  useHotkeys(
    "enter",
    (e) => {
      e.preventDefault();
      if (onTargetSelect) {
        onTargetSelect(focusedPosition);
      }
    },
    { enabled: isSelectingMoveTarget && !!onTargetSelect },
    [focusedPosition, onTargetSelect, isSelectingMoveTarget],
  );

  // cancel target selection with Escape
  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      onCancel();
    },
    { enabled: isSelectingMoveTarget },
    [onCancel, isSelectingMoveTarget],
  );

  // cancel target selection with 'm' key (toggle off)
  useHotkeys(
    "m",
    (e) => {
      e.preventDefault();
      onCancel();
    },
    { enabled: isSelectingMoveTarget },
    [onCancel, isSelectingMoveTarget],
  );

  return { focusedPosition };
};
